import { UserGroupIcon } from "@/assets/icons";
import { setUserGroupHeader } from "@/helpers/api_helper";
import { setUserGroup } from "@/modules/app/appSlice";
import { useAppDispatch, useAppState } from "@/modules/app/useAppContext";
import { Alert, Combobox } from "@altimateai/lego";
import { useEffect, useMemo, useState, useCallback } from "react";
import useUserGroups from "./useUserGroups";
import { useQueryClient } from '@tanstack/react-query';
import { isOwner } from "@/services/auth";
import { useLocation } from "react-router-dom";

export const LocalStorageKey = "userGroupId";
const UserGroupSelector = () => {
  const { rbacEnabled, userGroup, accessRulesDisabled } = useAppState();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [_refresh, setRefresh] = useState(Date.now());
  const [showTeamSelector, setShowTeamSelector] = useState(true);
  const { pathname } = useLocation();

  const {
    groups,
    isFetching,
    isLoading,
    search,
    setSearch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useUserGroups();

  const updateUserGroup = useCallback(
    (value: string) => {
      const selectedUserGroup = groups.find(item => item.id === value);
      dispatch(setUserGroup(selectedUserGroup));
    },
    [groups, dispatch]
  );

  const options = (isOwner() ? [{ label: "Default", value: "-1" }] : []).concat(
    groups.map(item => ({
      label: item.name,
      value: item.id,
    }))
  );

  // Add loading indicator at the end if fetching more pages
  const optionsWithLoader = [...options];
  if (isFetchingNextPage) {
    optionsWithLoader.push({
      label: "Loading more teams...",
      value: "loading",
    });
  }

  const selectedUserGroup = useMemo(() => {
    if (userGroup) {
      return options.find(group => group.value === userGroup.id);
    }

    const lsValue = localStorage.getItem(LocalStorageKey);
    if (!lsValue) {
      if (options.length === 1) {
        return options[0];
      }
      return null;
    }
    return options.find(group => group.value === lsValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGroup, options, _refresh]);

  useEffect(() => {
    if (accessRulesDisabled) {
      localStorage.removeItem(LocalStorageKey);
    }
  }, [accessRulesDisabled]);

  // If RBAC is enabled and team is not set, set the first team
  useEffect(() => {
    if (!rbacEnabled || !options.length) {
      return;
    }

    // On page load, set team from local storage or first available option
    const storedUserGroupId = localStorage.getItem(LocalStorageKey);
    updateUserGroup(storedUserGroupId || options[0].value);
  }, [rbacEnabled, options, updateUserGroup]);

  useEffect(() => {
    // Set team header for future sessions, but only after all pages are loaded
    // This prevents RBAC from filtering subsequent page requests
    if (!selectedUserGroup?.value || hasNextPage) {
      return;
    }
    setUserGroupHeader(selectedUserGroup.value);
    queryClient.invalidateQueries({
      predicate: query => query.queryKey?.[0] !== "UserGroups",
    });
  }, [selectedUserGroup?.value, hasNextPage, queryClient]);

  const currentUserGroup = useMemo(() => {
    if (!options.length || !selectedUserGroup?.value) {
      return null;
    }
    return options.find(item => item.value === selectedUserGroup.value);
  }, [options, selectedUserGroup?.value]);

  const onUserGroupSelect = (value: string | string[]) => {
    // Handle both single and multi-select cases
    const selectedValue = Array.isArray(value) ? value[0] : value;

    // Ignore selection of loading indicator
    if (selectedValue === "loading") return;

    const previousValue = localStorage.getItem(LocalStorageKey);
    localStorage.setItem(LocalStorageKey, selectedValue);
    updateUserGroup(selectedValue);
    setRefresh(Date.now());

    // On an actual team change, hard-reload so every page/query refetches
    // scoped to the new team. The header is re-initialized from localStorage at
    // bootstrap (see api_helper), so the reloaded page is RBAC-scoped from its
    // first request — more reliable than invalidating individual queries while
    // component-local filter state lingers.
    if (previousValue !== selectedValue) {
      window.location.reload();
    }
  };

  useEffect(() => {
    // Hide team selector on specific paths
    if (pathname.includes("/team-spends")) {
      setShowTeamSelector(false);
    } else {
      setShowTeamSelector(true);
    }
  }, [pathname]);

  // Clear search on mount to ensure we load all teams
  useEffect(() => {
    // Only clear search if it's not already empty to avoid infinite loops
    if (search && search.trim() !== "") {
      setSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // Auto-load all available data
  useEffect(() => {
    // Keep fetching until all pages are loaded, but only when search is empty
    if (hasNextPage && !isFetchingNextPage && search === "") {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, search]);

  // Only hide component during initial loading, not during pagination
  // Hide if accessRulesDisabled or rbacEnabled is false
  if (accessRulesDisabled || !rbacEnabled || isLoading) {
    return null;
  }

  if (!options.length && !isFetching) {
    return (
      <Alert variant="destructive" className="al-mt-3">
        You are not part of any team. Please contact your AltimateAI administrator to get access.
      </Alert>
    );
  }

  return (
    <div className={showTeamSelector ? "" : "al-invisible"}>
      <Combobox
        id="user-group-selector"
        value={selectedUserGroup?.value}
        options={optionsWithLoader}
        onChange={onUserGroupSelect}
        placeholder={currentUserGroup?.label || "Select Team"}
        buttonProps={{ size: "sm", variant: "secondary" }}
        icon={<UserGroupIcon />}
      />
    </div>
  );
};

export default UserGroupSelector;
